import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { obterOuCriarSessionId } from '@/shared/lib/jukeboxSession';
import type { RealtimeChannel } from '@supabase/supabase-js';

export const EMOJIS_REACAO      = ['❤️', '🔥', '💃', '🎵', '🙌'] as const;
export const EMOJIS_COPA_REACAO = ['🇧🇷', '⚽', '🏆', '🎉', '🔥'] as const;
const TODOS_EMOJIS              = [...EMOJIS_REACAO, ...EMOJIS_COPA_REACAO] as const;
export type EmojiReacao = typeof TODOS_EMOJIS[number];

export interface ReacaoPayload {
  emoji: EmojiReacao;
  session_id?: string;
}

// ── Emissor ───────────────────────────────────────────────────────────────────

/**
 * useReacaoEmissor — envia reações ao vivo via broadcast efêmero.
 * Debounce de 500 ms por emoji para evitar flood.
 * Canal destruído no hidden (Bom Cidadão de Rede) e no unmount.
 */
export function useReacaoEmissor(telaId: string | null | undefined) {
  const channelRef   = useRef<RealtimeChannel | null>(null);
  const lastSentRef  = useRef<Record<string, number>>({});
  // Mesmo motivo de useComandoChannel: o hidden dispara removeChannel e o
  // visible recria o tópico `reacao:{telaId}` antes do close.
  const remocaoRef   = useRef<Promise<unknown>>(Promise.resolve());
  const remocaoProntaRef = useRef(true);
  const geracaoRef   = useRef(0);

  const destruirCanal = useCallback(() => {
    geracaoRef.current++;
    if (channelRef.current) {
      remocaoProntaRef.current = false;
      const pendente = Promise.resolve(supabase.removeChannel(channelRef.current)).catch(() => {});
      remocaoRef.current = pendente;
      void pendente.then(() => {
        if (remocaoRef.current === pendente) remocaoProntaRef.current = true;
      });
      channelRef.current = null;
    }
  }, []);

  const criarCanal = useCallback(async () => {
    if (!telaId) return;
    destruirCanal();
    const geracao = geracaoRef.current;
    if (!remocaoProntaRef.current) await remocaoRef.current;
    if (geracao !== geracaoRef.current) return;
    const ch = supabase.channel(`reacao:${telaId}`);
    ch.subscribe();
    channelRef.current = ch;
  }, [telaId, destruirCanal]);

  useEffect(() => {
    void criarCanal();
    return destruirCanal;
  }, [criarCanal, destruirCanal]);

  useEffect(() => {
    if (!telaId) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        destruirCanal();
      } else {
        void criarCanal();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [telaId, criarCanal, destruirCanal]);

  const enviarReacao = useCallback((emoji: EmojiReacao) => {
    const agora = Date.now();
    if ((agora - (lastSentRef.current[emoji] ?? 0)) < 500) return;
    lastSentRef.current[emoji] = agora;
    channelRef.current?.send({
      type:    'broadcast',
      event:   'reacao',
      payload: { emoji, session_id: obterOuCriarSessionId(telaId!) } satisfies ReacaoPayload,
    });
  }, [telaId]);

  return { enviarReacao };
}

// ── Receptor ──────────────────────────────────────────────────────────────────

/**
 * useReacaoReceptor — escuta reações ao vivo no canal broadcast da tela.
 * Usa padrão cbRef para TVs 24/7.
 * Canal destruído no unmount.
 */
export function useReacaoReceptor(
  telaId: string | null | undefined,
  onReacao: (payload: ReacaoPayload) => void,
): void {
  const cbRef = useRef(onReacao);
  useEffect(() => { cbRef.current = onReacao; });

  useEffect(() => {
    if (!telaId) return;
    const ch = supabase
      .channel(`reacao:${telaId}`)
      .on('broadcast', { event: 'reacao' }, ({ payload }) => {
        const p = payload as ReacaoPayload;
        if (TODOS_EMOJIS.includes(p?.emoji as EmojiReacao)) {
          cbRef.current(p);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [telaId]);
}
